import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import { DisclaimerModal } from "@/components/DisclaimerModal";
import GAPageTracker from "@/components/GAPageTracker";
import SupportWidget from "@/components/SupportWidget";
import BetaBanner from "@/components/BetaBanner";

// EAGER — public landing & auth pages (fast first paint, SEO-critical)
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import InstallPage from "@/pages/InstallPage";

// LAZY — authenticated / heavy pages (loaded on demand to shrink initial bundle)
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const QuickStartThreePage = lazy(() => import("@/pages/QuickStartThreePage"));
const AnonymousChatPage = lazy(() => import("@/pages/AnonymousChatPage"));
const MagicLinkVerifyPage = lazy(() => import("@/pages/MagicLinkVerifyPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const GoalsPage = lazy(() => import("@/pages/GoalsPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("@/pages/SubscriptionPage").then(m => ({ default: m.SubscriptionSuccessPage })));
const DecisionsPage = lazy(() => import("@/pages/DecisionsPage"));
const JourneyPage = lazy(() => import("@/pages/JourneyPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const DemoRecordingPage = lazy(() => import("@/pages/DemoRecordingPage"));
const DemoPlayerOverlay = lazy(() => import("@/components/DemoPlayerOverlay"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const MasterProfilePage = lazy(() => import("@/pages/MasterProfilePage"));
const DeepenProfilePage = lazy(() => import("@/pages/DeepenProfilePage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const CheckinPage = lazy(() => import("@/pages/CheckinPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const InvitedPage = lazy(() => import("@/pages/InvitedPage"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const ShortLinkRedirect = lazy(() => import("@/pages/ShortLinkRedirect"));
const ShareStudioPage = lazy(() => import("@/pages/ShareStudioPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));

// Tiny fallback for lazy-loaded chunks
const RouteFallback = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
    <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Loading...</div>
  </div>
);

const Protected = ({ children, requireQuestionnaire = true, adminOnly = false }) => {
  const { user, loading, api, appConfig } = useApp();
  const [needsDisclaimer, setNeedsDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  useEffect(() => {
    if (!user) {
      setDisclaimerChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/disclaimer/status");
        if (!cancelled) setNeedsDisclaimer(!res.data.accepted);
      } catch (e) { /* fail open */ void e; }
      finally { if (!cancelled) setDisclaimerChecked(true); }
    })();
    return () => { cancelled = true; };
  }, [user?.id, api]);

  const acceptDisclaimer = async () => {
    try {
      await api.post("/disclaimer/acknowledge", { version: "2026-02-v1" });
      setNeedsDisclaimer(false);
    } catch (e) { /* keep open */ void e; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to={adminOnly ? "/admin/login" : "/login"} replace />;
  if (adminOnly && !user.admin_role && !user.is_admin) return <Navigate to="/admin/login" replace />;
  if (requireQuestionnaire && !user.questionnaire_completed) {
    // In beta_mode → minimal 3-question onboarding. Otherwise the full 39-question version.
    const target = appConfig?.beta_mode ? "/quick-start" : "/onboarding";
    return <Navigate to={target} replace />;
  }
  return (
    <>
      {children}
      {disclaimerChecked && <DisclaimerModal open={needsDisclaimer} onAccept={acceptDisclaimer} />}
    </>
  );
};

const PublicOnly = ({ children }) => {
  const { user, loading, appConfig } = useApp();
  if (loading) return null;
  if (user) {
    if (!user.questionnaire_completed) {
      return <Navigate to={appConfig?.beta_mode ? "/quick-start" : "/onboarding"} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" />
          <GAPageTracker />
          <BetaBanner />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/try" element={<AnonymousChatPage />} />
            <Route path="/probar" element={<AnonymousChatPage />} />
            <Route path="/auth/magic" element={<MagicLinkVerifyPage />} />
            <Route path="/quick-start" element={<Protected requireQuestionnaire={false}><QuickStartThreePage /></Protected>} />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/app" element={<InstallPage />} />
            <Route path="/get" element={<InstallPage />} />
            <Route path="/android" element={<InstallPage forcePlatform="android" />} />
            <Route path="/iphone" element={<InstallPage forcePlatform="ios" />} />
            <Route path="/ios" element={<InstallPage forcePlatform="ios" />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/brand" element={<AboutPage />} />
            <Route path="/acerca" element={<AboutPage />} />
            <Route path="/terms" element={<LegalPage initialTab="terms" />} />
            <Route path="/privacy" element={<LegalPage initialTab="privacy" />} />
            <Route path="/invitado" element={<InvitedPage />} />
            <Route path="/invited" element={<InvitedPage />} />
            <Route path="/auth/google/callback" element={<AuthCallback />} />
            <Route path="/p/:shortId" element={<ShortLinkRedirect />} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
            <Route path="/reset-password/:token" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
            <Route path="/onboarding" element={<Protected requireQuestionnaire={false}><OnboardingPage /></Protected>} />
            <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/chat" element={<Protected><ChatPage /></Protected>} />
            <Route path="/decisions" element={<Protected><DecisionsPage /></Protected>} />
            <Route path="/goals" element={<Protected><GoalsPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/journey" element={<Protected><JourneyPage /></Protected>} />
            <Route path="/share" element={<Protected><ShareStudioPage /></Protected>} />
            <Route path="/posts" element={<Protected><ShareStudioPage /></Protected>} />
            <Route path="/invitar" element={<Protected requireQuestionnaire={false}><InvitePage /></Protected>} />
            <Route path="/invite" element={<Protected requireQuestionnaire={false}><InvitePage /></Protected>} />
            <Route path="/checkin" element={<Protected><CheckinPage /></Protected>} />
            <Route path="/profile" element={<Protected requireQuestionnaire={false}><ProfilePage /></Protected>} />
            <Route path="/profile/complete" element={<Protected><MasterProfilePage /></Protected>} />
            <Route path="/profile/deepen" element={<Protected><DeepenProfilePage /></Protected>} />
            <Route path="/admin" element={<Protected requireQuestionnaire={false} adminOnly={true}><AdminPage /></Protected>} />
            <Route path="/demo-recording" element={<Protected requireQuestionnaire={false}><DemoRecordingPage /></Protected>} />
            <Route path="/subscription" element={<Protected requireQuestionnaire={false}><SubscriptionPage /></Protected>} />
            <Route path="/subscription/success" element={<Protected requireQuestionnaire={false}><SubscriptionSuccessPage /></Protected>} />
            <Route path="/subscription/bonus-success" element={<Protected requireQuestionnaire={false}><SubscriptionSuccessPage kind="bonus_pack" /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <DemoPlayerOverlay />
          <SupportWidget />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
